import React, { useState, useEffect } from 'react';
import './login.temp.css';
import { getCookie, reloadToHost } from '../../utils/UtilFunctionsV1';
import toast from 'react-hot-toast';
import LeftPanel from './components/LeftPanel';
import FormHeader from './components/FormHeader';
import Separator from './components/Separator';
import FormContent from './components/FormContent';
import GoogleLoginButtonV2 from './components/GoogleLoginButtonV2';
import { useMutationCall } from '../../api/react-client';
import { useClient } from '../../api/api-client';
import {
  GOOGLE_LOGIN,
  SEND_OTP_API,
  SUBMIT_OTP_API,
  SUBMIT_TOTP_API,
} from './constants.login';
import { LifelineLoginEvent } from '../../analytics/login-logout/events';
import { MetricsForJumboEvent } from '../../analytics/constants';
import { ERROR_CODES, ERROR_REASONS } from './error_code';
import { IsUserEmailError } from './login.utils';

const Login = () => {
  const client = useClient();
  const urlParams = new URLSearchParams(window.location.search);
  const redirectUrl = urlParams.get('redirect_url') || '/';
  const isSmartplugMode = redirectUrl.includes('smartplug');
  const lastUser = localStorage.getItem('lastLoggedInUser');
  const firstName = lastUser?.split(' ')[0];
  const greetingText =
    firstName && firstName !== 'undefined'
      ? `Welcome back, ${firstName}!`
      : 'Log in to continue';

  const [email, setEmail] = useState(
    localStorage.getItem('lastLoggedInEmail') || '',
  );
  const [otp, setOtp] = useState('');
  const [totp, setTotp] = useState('');

  const [step, setStep] = useState('email'); // email | otp | totp
  const [loading, setLoading] = useState(false);

  const [isValidEmail, setIsValidEmail] = useState(() => {
    const savedEmail = localStorage.getItem('lastLoggedInEmail') || '';
    // eslint-disable-next-line no-useless-escape
    return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(savedEmail);
  });
  const [isValidOTP, setIsValidOTP] = useState(false);
  const [isValidTOTP, setIsValidTOTP] = useState(false);
  const [headerText, setHeaderText] = useState(greetingText);
  const [displayedHeaderText, setDisplayedHeaderText] = useState(headerText);
  const [currentHeaderText, setCurrentHeaderText] = useState(null);
  const [animationKey, setAnimationKey] = useState(0);
  const [googleLoginLoading, setGoogleLoginLoading] = useState(false);
  const [csrfReady, setCsrfReady] = useState(false);

  const validateEmail = (value) => {
    setEmail(value);
    // eslint-disable-next-line no-useless-escape
    const valid = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value);
    setIsValidEmail(valid);
  };

  const validateTOTP = (value) => {
    setTotp(value);
    setIsValidTOTP(value.length === 6);
  };

  const { mutate: getOtp } = useMutationCall(
    {
      onSuccess: () => {
        LifelineLoginEvent({
          [MetricsForJumboEvent.customer_id]: email,
          [MetricsForJumboEvent.feature_meta]: {
            auth_method: 'OTP',
            action: 'FETCH_OTP_API',
            email: email,
            success: true,
          },
        });
        setStep('otp');
        toast.success('OTP sent!');
      },
      onError: (err) => {
        LifelineLoginEvent({
          [MetricsForJumboEvent.customer_id]: email,
          [MetricsForJumboEvent.feature_meta]: {
            auth_method: 'OTP',
            action: 'FETCH_OTP_API',
            email: email,
            success: false,
            error_message: err?.message || 'Failed to send OTP | Source: FE',
          },
        });
      },
    },
    SEND_OTP_API,
    ['SEND_OTP_API'],
    {
      doNotShowSuccessToast: true,
    },
    () => {
      setIsValidOTP(false);
      return client(SEND_OTP_API, {
        data: { email },
      });
    },
  );

  const getTOtp = () => {
    setIsValidTOTP(false);
    setStep('totp');
  };

  const submitSuccessHandler = (res, auth_method, action) => {
    const { success, redirect_url: resRedirectUrl } = res || {};
    if (success) {
      LifelineLoginEvent({
        [MetricsForJumboEvent.customer_id]: email,
        [MetricsForJumboEvent.feature_meta]: {
          auth_method: auth_method,
          action: action,
          email: email,
          success: true,
          redirect_url: resRedirectUrl || 'N/A | Source: FE',
        },
      });
      if (resRedirectUrl) {
        window.location.href = resRedirectUrl;
      } else {
        reloadToHost();
      }
    } else {
      reloadToHost();
    }
    setLoading(false);
    setIsValidOTP(false);
    setIsValidTOTP(false);
  };

  const { mutate: submitOtp } = useMutationCall(
    {
      onSuccess: (res) => submitSuccessHandler(res, 'OTP', 'SUBMIT_OTP_API'),
      onError: (err) => {
        setLoading(false);
        setIsValidOTP(false);
        LifelineLoginEvent({
          [MetricsForJumboEvent.customer_id]: email,
          [MetricsForJumboEvent.feature_meta]: {
            auth_method: 'OTP',
            action: 'SUBMIT_OTP_API',
            email: email,
            success: false,
            error_message:
              err?.message || `Failed to validate/submit OTP | Source: FE`,
          },
        });
      },
    },
    SUBMIT_OTP_API,
    ['SUBMIT_OTP_API'],
    {
      doNotShowSuccessToast: true,
    },
    () => {
      setLoading(true);
      return client(SUBMIT_OTP_API, {
        data: {
          email,
          otp,
          redirect_url: redirectUrl,
        },
      });
    },
  );

  const { mutate: submitTOtp } = useMutationCall(
    {
      onSuccess: (res) => submitSuccessHandler(res, 'TOTP', 'SUBMIT_TOTP_API'),
      onError: (err) => {
        setLoading(false);
        setIsValidTOTP(false);
        LifelineLoginEvent({
          [MetricsForJumboEvent.customer_id]: email,
          [MetricsForJumboEvent.feature_meta]: {
            auth_method: 'TOTP',
            action: 'SUBMIT_TOTP_API',
            email: email,
            success: false,
            error_message:
              err?.message || `Failed to validate/submit TOTP | Source: FE`,
          },
        });
      },
    },
    SUBMIT_TOTP_API,
    ['SUBMIT_TOTP_API'],
    {
      doNotShowSuccessToast: true,
    },
    () => {
      setLoading(true);
      return client(SUBMIT_TOTP_API, {
        data: {
          email,
          otp: totp,
          redirect_url: redirectUrl,
        },
      });
    },
  );

  const onGoogleSuccess = async (credentialResponse) => {
    if (!csrfReady) {
      return;
    }

    let csrfToken = getCookie('csrf_token');

    if (!csrfToken) {
      // retry fetching CSRF
      await fetch(GOOGLE_LOGIN, {
        method: 'GET',
        credentials: 'include',
      });
      csrfToken = getCookie('csrf_token');
      if (csrfToken) {
        setCsrfReady(true);
      }
    }

    if (!csrfToken) {
      toast.error(
        'Please try again later or use OTP/TOTP | Error code: ' +
          ERROR_CODES[ERROR_REASONS.FETCH_CSRF_TOKEN_NO_EXIST],
      );
      LifelineLoginEvent({
        [MetricsForJumboEvent.feature_meta]: {
          auth_method: 'GOOGLE',
          action: 'FETCH_GOOGLE_CSRF_TOKEN_NO_EXIST',
          success: false,
          error_message: ERROR_REASONS.FETCH_CSRF_TOKEN_NO_EXIST,
        },
      });
      return;
    }

    const requestPayload = {
      credential: credentialResponse?.credential,
      redirect_url: redirectUrl,
      csrf_token: csrfToken,
    };

    try {
      setGoogleLoginLoading(true);
      const response = await fetch(GOOGLE_LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestPayload),
      });
      const res = await response.json();

      if (res?.success) {
        let decodedPayload = {};
        try {
          decodedPayload =
            JSON.parse(
              atob(credentialResponse?.credential?.split('.')?.[1] || ''),
            ) || {};
        } catch {
          decodedPayload = {};
        }
        const decoded = decodedPayload;
        LifelineLoginEvent({
          [MetricsForJumboEvent.feature_meta]: {
            auth_method: 'GOOGLE',
            action: ERROR_REASONS.VALIDATE_GOOGLE_CSRF_TOKEN_TRY,
            success: true,
            email_from_google: decoded?.email || 'N/A | Source: FE',
            name_from_google: decoded?.name || 'N/A | Source: FE',
          },
        });
        const userImage = decoded.picture;
        localStorage.setItem('userImage', JSON.stringify(userImage));
        if (res?.redirect_url) {
          window.location.href = res.redirect_url;
        } else {
          reloadToHost();
        }
      } else {
        if (IsUserEmailError(res)) {
          LifelineLoginEvent({
            [MetricsForJumboEvent.feature_meta]: {
              auth_method: 'GOOGLE',
              action: ERROR_REASONS.VALIDATE_GOOGLE_CSRF_TOKEN_TRY,
              success: true,
              response: JSON.stringify(res),
            },
          });
          toast.error(
            res?.error ||
              res?.message ||
              'Please try again later or use OTP/TOTP | Email not found.',
          );
        } else {
          LifelineLoginEvent({
            [MetricsForJumboEvent.feature_meta]: {
              auth_method: 'GOOGLE',
              action: ERROR_REASONS.VALIDATE_GOOGLE_CSRF_TOKEN_TRY,
              success: false,
              response: JSON.stringify(res),
              error_message:
                res?.error ||
                res?.message ||
                ERROR_REASONS.VALIDATE_GOOGLE_CSRF_TOKEN_TRY,
            },
          });
          toast.error(
            'Please try again later or use OTP/TOTP | Error code: ' +
              ERROR_CODES[ERROR_REASONS.VALIDATE_GOOGLE_CSRF_TOKEN_TRY],
          );
        }
      }
      setGoogleLoginLoading(false);
    } catch (e) {
      setGoogleLoginLoading(false);
      LifelineLoginEvent({
        [MetricsForJumboEvent.feature_meta]: {
          auth_method: 'GOOGLE',
          action: ERROR_REASONS.VALIDATE_GOOGLE_CSRF_TOKEN_FAILED_CATCH,
          success: false,
          error_message:
            e.error || ERROR_REASONS.VALIDATE_GOOGLE_CSRF_TOKEN_FAILED_CATCH,
        },
      });
      toast.error(
        'Please try again later or use OTP/TOTP | Error code: ' +
          ERROR_CODES[ERROR_REASONS.VALIDATE_GOOGLE_CSRF_TOKEN_FAILED_CATCH],
      );
    } finally {
      setLoading(false);
    }
  };

  const validateOTP = (value) => {
    setOtp(value);
    setIsValidOTP(value.length === 10);
  };

  const goBack = () => {
    setStep('email');
    setOtp('');
    setTotp('');
  };

  useEffect(() => {
    if (step === 'email') {
      setHeaderText(greetingText);
    } else if (step === 'otp') {
      setHeaderText('OTP Verification');
    } else if (step === 'totp') {
      setHeaderText('Auth App Verification');
    }
  }, [step, greetingText]);

  useEffect(() => {
    const newText = headerText;

    if (newText !== displayedHeaderText) {
      setCurrentHeaderText(displayedHeaderText);
      setDisplayedHeaderText(newText);
      setAnimationKey((k) => k + 1);

      const timer = setTimeout(() => {
        setCurrentHeaderText(null);
      }, 350);

      return () => clearTimeout(timer);
    }
  }, [headerText, displayedHeaderText]);

  useEffect(() => {
    const fetchCsrf = async () => {
      try {
        const res = await fetch(GOOGLE_LOGIN, {
          method: 'GET',
          credentials: 'include',
        });
        if (!res.ok) {
          toast.error(
            `Please try again later or use OTP/TOTP | Error code: ${
              ERROR_CODES[ERROR_REASONS.FETCH_CSRF_TOKEN_NOT_OK]
            }`,
          );
          LifelineLoginEvent({
            [MetricsForJumboEvent.feature_meta]: {
              auth_method: 'GOOGLE',
              action: 'FETCH_GOOGLE_CSRF_TOKEN',
              success: false,
              error_message: ERROR_REASONS.FETCH_CSRF_TOKEN_NOT_OK,
            },
          });
        } else {
          const csrfToken = getCookie('csrf_token');
          LifelineLoginEvent({
            [MetricsForJumboEvent.feature_meta]: {
              auth_method: 'GOOGLE',
              action: 'FETCH_GOOGLE_CSRF_TOKEN',
              success: true,
              csrf_token_fetched: !!csrfToken,
            },
          });
        }
        setCsrfReady(true);
      } catch (e) {
        console.error('CSRF fetch error:', e);
        LifelineLoginEvent({
          [MetricsForJumboEvent.feature_meta]: {
            auth_method: 'GOOGLE',
            action: 'FETCH_GOOGLE_CSRF_TOKEN',
            success: false,
            error_message:
              e?.message || e?.error || ERROR_REASONS.FETCH_CSRF_TOKEN,
          },
        });
        toast.error(
          `Please login using OTP/TOTP | Error code: ${
            ERROR_CODES[ERROR_REASONS.FETCH_CSRF_TOKEN]
          }`,
        );
      }
    };

    fetchCsrf();
  }, []);

  return (
    <div className='tw-h-full'>
      <LeftPanel isSmartplugMode={isSmartplugMode} />

      {/* RIGHT PANEL */}
      <div
        className={`tw-w-2/5 tw-flex-col tw-items-center tw-justify-center tw-p-32 ${
          isSmartplugMode ? 'tw-flex-1' : 'tw-flex'
        }`}
      >
        <div className='tw-flex tw-flex-col tw-w-full'>
          <FormHeader
            currentHeaderText={currentHeaderText}
            animationKey={animationKey}
            displayedHeaderText={displayedHeaderText}
            step={step}
            goBack={goBack}
          />

          <FormContent
            step={step}
            email={email}
            validateEmail={validateEmail}
            isValidEmail={isValidEmail}
            getOtp={getOtp}
            getTOtp={getTOtp}
            totp={totp}
            validateTOTP={validateTOTP}
            isValidTOTP={isValidTOTP}
            submitTOtp={submitTOtp}
            otp={otp}
            validateOTP={validateOTP}
            isValidOTP={isValidOTP}
            submitOtp={submitOtp}
            loading={loading}
          />

          <Separator />

          {!isSmartplugMode ? (
            <GoogleLoginButtonV2
              disabled={!csrfReady}
              loading={googleLoginLoading}
              onSuccess={onGoogleSuccess}
              onError={() => {
                LifelineLoginEvent({
                  [MetricsForJumboEvent.feature_meta]: {
                    auth_method: 'GOOGLE',
                    action: 'GOOGLE_SDK_LOGIN',
                    success: false,
                    error_message: ERROR_REASONS.GOOGLE_LOGIN_FAILED_SDK,
                  },
                });
                toast.error(
                  `Please try again later or use OTP/TOTP | Error code: ${
                    ERROR_CODES[ERROR_REASONS.GOOGLE_LOGIN_FAILED_SDK]
                  }`,
                );
              }}
            />
          ) : (
            <a
              href='https://lifeline.blinkit.com/login?redirect_url=%2Flifeline'
              target='_blank'
              rel='noopener noreferrer'
              className='tw-mx-auto tw-font-medium tw-text-blue-500 tw-cursor-pointer hover:tw-underline'
            >
              Go to Lifeline Dashboard for Google SSO
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
